




























































































































import { Component, Vue } from 'vue-property-decorator';
import { mdbBtn, mdbIcon, mdbTooltip, mdbProgress } from '@folky-tools/mdbvue';
import { VueGoodTable } from '@folky-tools/vue-good-table';
import { usersService, axiosService, authorizationManagerService, associationsService, userInvitationsService, notificationsService, appService } from '@/services';
import { Association } from '@/models/associations/association';
import { EntityOwner } from '@/models/common/entity-owner';
import { Invitation } from '@/models/users/user-invitations/invitation';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';

@Component({
    components: {
        mdbBtn,
        mdbIcon,
        mdbTooltip,
        mdbProgress,
        VueGoodTable,
        FormModal: () => import('@/components/shared/FormModal.vue'),
        ChangeEntityOwner: () => import('@/components/shared/ChangeEntityOwner.vue'),
        AppSysBar: () => import('@/components/shared/AppSysBar.vue')
    }
})
export default class AssociationListView extends Vue {
    private associationsPerPage: number = 20;
    private owner: EntityOwner = new EntityOwner();
    private showChangeOwner: boolean = false;
    private savingNewOwner: boolean = false;
    private sendingNewOwnerInvitation: boolean = false;
    private actualUserCanCreateAssociations: boolean = false;
    private rows: any[] = [];
    private columns: any[] = [];
    private totalRecords: number = 0;
    private isLoading: boolean = false;
    private serverParams: any = {
        columnFilters: {
            name: ''
        },
        sort: [
            {
                field: 'id',
                type: 'desc'
            }
        ],
        page: 1,
        perPage: 20
    };

    private async beforeMount(): Promise<void> {
        await this.setColumns();
        await this.loadItems();
    }

    private async mounted(): Promise<void> {
        this.$root.$emit('change-active-page', 'views.associations.associationListView.title');
        this.actualUserCanCreateAssociations = authorizationManagerService.canCreateAssociations;
        await usersService.logUserAction(
            new UserActionLogInfo({
                type: UserActionType.HasVisited,
                entity: UserActionEntityType.Associations,
                scope: UserActionScopeType.List
            })
        );
    }

    private getQueryParams(sortOrder: any, currentPage: number, perPage: number): any {
        return {
            $skip: (currentPage - 1) * perPage,
            $top: perPage,
            $orderby: sortOrder && sortOrder.length === 1 ? `${sortOrder[0].field} ${sortOrder[0].type}` : null,
            $filter: this.getQueryFilterFromColumns()
        };
    }

    private updateParams(newProps: any): void {
        this.serverParams = { ...this.serverParams, ...newProps };
    }

    private async onPageChange(params: any): Promise<void> {
        this.updateParams({ page: params.currentPage });
        await this.loadItems();
    }

    private async onSortChange(params: any): Promise<void> {
        this.updateParams({
            sort: [
                {
                    type: params[0].type,
                    field: params[0].field
                }
            ]
        });
        await this.loadItems();
    }

    private async onColumnFilter(params: any): Promise<void> {
        this.serverParams.page = 1;
        this.updateParams(params);
        await this.loadItems();
    }

    private async onPerPageChange(params: any): Promise<void> {
        this.updateParams({ perPage: params.currentPerPage });
        await this.loadItems();
    }

    private async loadItems(): Promise<void> {
        this.isLoading = true;

        const response: any = await axiosService.instance.get('associations/organization/query', {
            params: this.getQueryParams(this.serverParams.sort, this.serverParams.page, this.serverParams.perPage)
        });

        this.rows = [];

        this.totalRecords = response.data.count;
        response.data.items.forEach((item: any) => {
            return this.rows.push(new Association(item));
        });

        this.isLoading = false;
    }

    private getQueryFilterFromColumns(): string | null {
        let queryFilter = '';
        const logicalOperatorBetweenFilters = ' and ';

        for (const key of Object.keys(this.serverParams.columnFilters)) {
            if (this.serverParams.columnFilters[key]) {
                if (key === 'name') {
                    queryFilter += `${logicalOperatorBetweenFilters}contains(name, '${this.serverParams.columnFilters[key].replace('\'', '\'\'')}')`;
                }
            }
        }

        return queryFilter === '' ? null : queryFilter.substring(logicalOperatorBetweenFilters.length);
    }

    private setColumns(): void {
        this.columns = [
            { field: 'id', type: 'number', sortable: true },
            {
                field: 'name',
                sortable: true,
                type: 'text',
                filterOptions: {
                    enabled: true,
                    placeholder: this.$t('common.placeholders.searchByName'),
                    filterValue: ''
                }
            },
            { field: 'ownerEmail', sortable: false, type: 'text' },
            { field: 'projectName', sortable: false, type: 'text' },
            { field: 'pricingName', sortable: false, type: 'text' },
            { field: 'isPublished', sortable: false }
        ];
    }

    private changeAssociationOwner(association: Association): void {
        this.owner = new EntityOwner({
            id: association.ownerId,
            email: association.ownerEmail,
            managedEntityId: association.id,
            managedEntityProjectIds: [association.projectId]
        });

        this.showChangeOwner = true;
    }

    private async inviteAssociationOwner(owner: EntityOwner): Promise<void> {
        this.sendingNewOwnerInvitation = true;
        try {
            const currentApplication = appService.getCurrentApplication();
            const associationOwnerInvitation: Invitation = new Invitation({
                projectIds: [...owner.managedEntityProjectIds],
                associationId: owner.managedEntityId,
                email: owner.email,
                destinationApplicationId: currentApplication.id,
                destinationApplicationClientId: currentApplication.clientId,
                destinationApplicationName: `${currentApplication.clientName} (${currentApplication.clientUri})`,
                destinationApplicationUri: currentApplication.clientUri
            });

            if ((await userInvitationsService.newInvitation(associationOwnerInvitation)) && (await associationsService.updateAssociationInvitedOwnerEmail(owner.managedEntityId, owner.email!))) {
                await this.loadItems();
                this.closeChangeAssociationOwner();

                notificationsService.showToastSuccessNotification('views.associations.associationListView.notifications.invitationSent.successMessage');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasChanged,
                        entity: UserActionEntityType.Associations,
                        scope: UserActionScopeType.Owner,
                        entityId: owner.managedEntityId
                    })
                );

                return;
            }

            notificationsService.showToastErrorNotification();
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.sendingNewOwnerInvitation = false;
        }
    }

    private async saveAssociationNewOwner(): Promise<void> {
        this.savingNewOwner = true;

        if (await (this.$refs.changeEntityOwner as any).hasErrors()) {
            this.savingNewOwner = false;
            return;
        }

        try {
            if (await associationsService.updateAssociationOwner(this.owner.managedEntityId, this.owner.id, this.owner.email)) {
                this.closeChangeAssociationOwner();
                notificationsService.showToastSuccessNotification('views.associations.associationListView.notifications.changeOwner.successMessage');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasChanged,
                        entity: UserActionEntityType.Associations,
                        scope: UserActionScopeType.Owner,
                        entityId: this.owner.managedEntityId
                    })
                );

                return;
            }
            notificationsService.showToastErrorNotification();
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.savingNewOwner = false;
        }
    }

    private closeChangeAssociationOwner(): void {
        this.showChangeOwner = false;
    }
}
